/* eslint-disable @next/next/no-img-element */
import {useState, useEffect, useRef} from 'react';
import {Drawer, Select, Progress, Row, Tooltip, Spin} from 'antd';
import {useStore} from '@/store/root-store';
import {observer} from 'mobx-react';
import styled from 'styled-components';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowUp, faFileDownload} from '@fortawesome/pro-regular-svg-icons';
import {getProgressColor} from '@/utils/colors';
import {CaretDownFilled, LoadingOutlined} from '@ant-design/icons';
import styles from './style.module.scss';
import html2canvas from 'html2canvas';
import {Button, TabPane, Tabs} from '@/components/common-components';
import {CompetitorsDrawerTab} from './CompetitorsDrawerTab/CompetitorsDrawerTab';
import {CloseOutlined} from '@ant-design/icons';
import {toJS} from 'mobx';
import {NextImg} from '@/utils/nextImg';


const squareTooltip = (hostname, url, keyword, score) => (
  <div style={{display: 'flex'}}>
    <div style={{marginRight: 10, marginTop: 7}}><NextImg style={{width: 30, height: 30}} src={`https://www.google.com/s2/favicons?sz=16&domain_url=${hostname}`} /></div>
    <div>
      <div style={{fontWeight: 500}}>{hostname}</div>
      <a className={styles.truncate} href={url} style={{fontSize: '12px'}}>{url}</a>
      <div style={{fontSize: '12px', marginTop: 5}}>
        <span>{`is `}</span>
        {score > 0 ? <IsRanking>ranking</IsRanking> : <IsNotRanking>not ranking</IsNotRanking>}
        <span>{`for `}</span>
        <span style={{textDecoration: 'underline'}}>{keyword}</span>
      </div>
      {/* <div>used <u>{keyword}</u> {score} times</div> */}
    </div>
  </div>
);
const focusTermsTooltip = (hostname, url, keyword, score) => (
  <div style={{display: 'flex'}}>
    <div style={{marginRight: 10, marginTop: 7}}><NextImg style={{width: 30, height: 30}} src={`https://www.google.com/s2/favicons?sz=16&domain_url=${hostname}`} /></div>
    <div>
      <div className={styles.truncate} style={{fontWeight: 500}}>{hostname}</div>
      <a className={styles.truncate} href={url} style={{fontSize: '12px'}}>{url}</a>
      <div style={{fontSize: '12px', marginTop: 5}}>
        <span>{score > 0 ? `uses ` : `did not use `}</span>
        <span style={{textDecoration: 'underline'}}>{keyword}</span>&nbsp;
        {score > 0 && <><IsRanking>{score}</IsRanking>
          <span>{score == 1 ? `time` : `times`}</span></>}
      </div>
    </div>
  </div>
);
export const CompetingFocusTermsDrawer = observer(() => {
  const {contentOptimizer: {currentPage}} = useStore('');
  const {settings: {customer: {profile: {isSubscriber}}}} = useStore('');
  const {contentOptimizer: {currentPage: {analytics, isCompetingFocusTermsDrawerOpen, closeCompetingFocusTermsDrawer, isOpeningKwSuggestionsFromContentResearch, setIsOpeningKwSuggestionFromContentResearch}}} = useStore('');
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [selectedCompTabKw, setSelectedCompTabKw] = useState<string>('');
  const [isHovered, setIsHovered] = useState(false);
  const [maxElemHeight, setMaxElemHeight] = useState(0);
  const allRef = useRef(null);
  const keywordSuggestionsRef = useRef(null);
  const [activeTab, setActiveTab] = useState('1');

  const antIcon = <LoadingOutlined style={{fontSize: 16, color: '#7f4ead', marginLeft: '10px'}} spin />;

  useEffect(() => {
    if (currentPage?.topRankingCompetitorActiveKw && currentPage?.analytics?.competitorsData?.length && currentPage?.analytics?.competitorsData.find(item => item.keyword == currentPage?.topRankingCompetitorActiveKw)) {
      setSelectedKeyword(currentPage?.topRankingCompetitorActiveKw);
      setSelectedCompTabKw(currentPage?.topRankingCompetitorActiveKw);
    } else if (currentPage?.analytics?.competitorsData && currentPage?.analytics?.competitorsData[0]) {
      setSelectedKeyword(currentPage?.analytics?.competitorsData[0]?.keyword);
      setSelectedCompTabKw(currentPage?.analytics?.competitorsData[0]?.keyword);
    } else {
      setSelectedKeyword('');
      setSelectedCompTabKw('');
    }
  }, [...(currentPage?.analytics?.competitorsData ?? ''), currentPage?.analytics?.competitorsData?.length]);

  const visibilityChangeHandler = () => {
    // for competitors tab (tab 1)
    if (currentPage?.topRankingCompetitorActiveKw) {
      setSelectedCompTabKw(currentPage?.topRankingCompetitorActiveKw);
    } else {
      setSelectedCompTabKw(currentPage?.analytics?.competitorsData[0]?.keyword || '');
    }

    // for focus terms grid
    if (!analytics?.focusTermsGridData?.length) return;
    if (currentPage?.topRankingCompetitorActiveKw) {
      setSelectedKeyword(currentPage?.topRankingCompetitorActiveKw);
    } else {
      setSelectedKeyword(analytics?.focusTermsGridData[0]?.keyword);
    }
  };

  useEffect(() => {
    if (isOpeningKwSuggestionsFromContentResearch) {
      setActiveTab('2');
    }
  }, [isOpeningKwSuggestionsFromContentResearch]);

  useEffect(() => {
    const elementsArray = document.querySelectorAll('.singleHostName');
    const elementsWidthArr = [];
    elementsArray.forEach(elem => elementsWidthArr.push(elem.clientWidth));
    setMaxElemHeight(Math.max(...elementsWidthArr));
  }, [document.querySelectorAll('.singleHostName'), selectedKeyword]);

  const exportSingleNode = node => {
    const today = new Date();
    const date = today.getFullYear()+'-'+(today.getMonth()+1)+'-'+today.getDate();
    html2canvas(node)
      .then(canvas => {
        canvas.style.display = 'none';
        const image = canvas.toDataURL();
        const a = document.createElement('a');
        a.setAttribute('download', `competing-focus-terms-for-'${selectedKeyword}'.png-${date}.png`);
        a.setAttribute('href', image);
        a.click();
      });
  };

  const onTabChange = key =>{
    setActiveTab(key);
  };

  const [competitors, setCompetitors] = useState(currentPage?.analytics?.competitorsData?.length ? currentPage?.analytics?.competitorsData[0]?.competitors : []);
  const [filteredCompetitorsData, setFilteredCompetitorsData] = useState(currentPage?.analytics?.competitorsData?.length ? currentPage?.analytics?.competitorsData[0]?.competitors : []);
  const [schemaTypesList, setSchemaTypesList] = useState([]);
  const [allSchemasCount, setAllSchemasCount] = useState(0);
  const [nullCount, setNullCount] = useState(0);
  const [selectedSchema, setSelectedSchema] = useState({
    idx: 'all-schemas',
    value: 'all-schemas',
  });

  function countOccurrences(inputArray) {
    const counts = {};
    const result = [];

    for (let i = 0; i < inputArray.length; i++) {
      const item = inputArray[i];

      if (typeof item === 'string' && item !== 'null') {
        if (counts[item]) {
          counts[item]++;
        } else {
          counts[item] = 1;
        }
      }
    }

    for (const word in counts) {
      if (Object.prototype.hasOwnProperty.call(counts, word)) {
        result.push({value: word, count: counts[word]});
      }
    }

    return result;
  }

  const schemaCountsHandler = () => {
    const competitorsTypes = [];
    competitors?.forEach(item => {
      if (item?.schema) {
        if (Array.isArray(item?.schema)) {
          item?.schema?.forEach(ele => {
            if (ele?.graph) {
              if (Array.isArray(ele?.graph)) {
                ele?.graph?.forEach(graphItem => {
                  if (Array.isArray(graphItem?.type)) {
                    graphItem?.type?.forEach(item => competitorsTypes.push(item));
                  } else {
                    competitorsTypes.push(graphItem?.type);
                  }
                });
              }
            } else {
              competitorsTypes.push(ele?.type);
            }
          });
        }
      }
    });
    setSchemaTypesList(countOccurrences(competitorsTypes));
    setAllSchemasCount(competitorsTypes?.length);

    let nullCount = 0;

    competitors?.map(item => {
      if (item?.schema?.length == 0 || item?.schema == null) {
        nullCount += 1;
      }
    });
    setNullCount(nullCount);
  };

  useEffect(() => {
    setFilteredCompetitorsData(toJS(currentPage?.analytics?.competitorsData)?.filter(item => item.keyword === selectedCompTabKw)[0]?.competitors);
    setCompetitors(toJS(currentPage?.analytics?.competitorsData)?.filter(item => item.keyword === selectedCompTabKw)[0]?.competitors);
    setSelectedSchema({
      idx: 'all-schemas',
      value: 'all-schemas',
    });
    if (competitors?.length) {
      schemaCountsHandler();
    }
  }, [selectedCompTabKw]);

  useEffect(() => {
    if (competitors?.length) {
      schemaCountsHandler();
    }
  }, [competitors]);

  return (
    <Drawer
      visible={isCompetingFocusTermsDrawerOpen}
      placement='right'
      afterVisibleChange={visibilityChangeHandler}
      width={'80vw'}
      onClose={() => {
        setActiveTab('1');
        setIsOpeningKwSuggestionFromContentResearch(false);
        closeCompetingFocusTermsDrawer();
      }}
      closeIcon={<CloseOutlined style={isCompetingFocusTermsDrawerOpen ? {display: 'block'} : {display: 'none'}}/>}
      className={styles.competingTermsDrawerStyle}
      bodyStyle={{backgroundColor: '#F2F2F5', paddingLeft: '0', paddingRight: '0'}}
    >
      <TitleStyled>Competitor Content Analysis</TitleStyled>
      <Tabs tabPosition={'top'} defaultActiveKey={'1'}
        activeKey={activeTab}
        className={styles.tabsContainer} onChange={key => onTabChange(key)}>
        <TabPane tab={<div>Top competitors <span style={{display: 'inline-block', marginLeft: 5}}>{currentPage?.isAnalyzing && <Spin indicator={antIcon} style={{marginRight: '20px'}}/>}</span></div>} key={'1'}>
          <CompetitorsDrawerTab
            activeKw={selectedCompTabKw}
            setActiveKw={setSelectedCompTabKw}
            isSubscriber={isSubscriber}
            setCompetitors={setCompetitors}
            competitors={competitors}
            filteredCompetitorsData={filteredCompetitorsData}
            setFilteredCompetitorsData={setFilteredCompetitorsData}
            schemaTypesList={schemaTypesList}
            setSchemaTypesList={setSchemaTypesList}
            allSchemasCount={allSchemasCount}
            setAllSchemasCount={setAllSchemasCount}
            nullCount={nullCount}
            setNullCount={setNullCount}
            selectedSchema={selectedSchema}
            setSelectedSchema={setSelectedSchema}/>
        </TabPane>
        <TabPane tab={<div>Relevant terms grid <span style={{display: 'inline-block', marginLeft: 5}}>{currentPage?.isAnalyzing && <Spin indicator={antIcon} style={{marginRight: '20px'}}/>}</span></div>} key={'2'}>
          <Wrapper ref={allRef} style={{minWidth: 1210, overflowX: 'scroll'}}>
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
              <div>
                <Title>Competing Focus Terms</Title>
                <Subtitle>Check what focus terms your competitors are using</Subtitle>
              </div>
              <Tooltip title='Export to PNG'>
                <Button
                  buttonType='transparent'
                  data-html2canvas-ignore={true}
                  onClick={() => exportSingleNode(allRef.current)}
                >
                  <FontAwesomeIcon icon={faFileDownload} size='2x'/>
                </Button>
              </Tooltip>
            </div>
            <SectionHeader style={{marginTop: 30}}>
              <span style={{marginRight: 125}}>COMPETITOR RANK</span>
              <div style={{display: 'flex', gap: 2, alignItems: 'center'}}>
                {analytics?.focusTermsGridData?.find(x => x?.keyword === selectedKeyword)?.sites?.map((x, i) => (
                  <Rank key={`rank-${x?.url}-${i}`}>{i === 0 ? 'YOU' : i}</Rank>
                ))}
              </div>
            </SectionHeader>
            <div style={{alignItems: 'start', textAlign: 'center', marginTop: 20, marginBottom: 20, marginLeft: 0, display: 'flex', gap: 2, flexDirection: 'column', transform: `rotate(-90deg) translate(0px, 243px)`, width: `${maxElemHeight}px`, maxHeight: `calc(${maxElemHeight}px - 19px)`, height: `calc(${140}px)`}}>
              {analytics?.focusTermsGridData?.find(x => x?.keyword === selectedKeyword)?.sites?.map((x, i) => {
                if (!x?.hostname) {
                  return (
                    <div key={`site-${x?.url}-${i}`} style={{display: 'flex', marginBottom: '10px', marginTop: '11px'}} className='singleHostName'>
                      <Site style={{color: '#121212'}}>
                        Created From Scratch
                      </Site>
                    </div>
                  );
                }
                return (
                  <a href={x?.url} target='_blank' rel='noreferrer' key={`site-${x?.url}-${i}`} style={{display: 'flex', marginBottom: '10px', marginTop: '11px'}} className='singleHostName'>
                    <NextImg src={`https://www.google.com/s2/favicons?sz=16&domain_url=${x.hostname}`} style={{transform: 'rotate(90deg)', width: '16px', height: '16px', marginRight: '5px'}}/>
                    <Site>
                      {x?.hostname}
                    </Site>

                  </a>
                );
              })}
            </div>
            <SectionHeader>
              <span style={{marginRight: 45}}>KEYWORD</span>
              <span>CONTENT SCORE</span>
              <span style={{marginLeft: 7}}><FontAwesomeIcon transform={{rotate: 140}} icon={faArrowUp} color='#A3A4A4' /></span>
            </SectionHeader>
            <Row wrap={false} style={{padding: '25px 10px'}}>
              {/* <div style={{width: 230, marginRight: 17}}>{selectedKeyword}</div> */}
              <div style={{width: 230, marginRight: 17}}>
                <SelectStyled suffixIcon={<CaretDownFilled />} value={selectedKeyword} onChange={value => setSelectedKeyword(value as string)}>
                  {analytics?.focusTermsGridData?.map((item, i) => (
                    <Select.Option key={`option-${item.keyword}-${i}`} value={item.keyword}>{item.keyword}</Select.Option>
                  ))}
                </SelectStyled>
              </div>
              <div style={{display: 'flex', gap: 12}}>
                {analytics?.focusTermsGridData?.find(x => x?.keyword === selectedKeyword)?.contentScores?.map((score, i) => (
                  <Progress
                    key={`progress-${i}`}
                    percent={score ?? 0}
                    strokeLinecap='round'
                    gapDegree={0}
                    trailColor='#ddd'
                    strokeColor={getProgressColor(score ?? 0, 25, 50, 75)}
                    showInfo={true}
                    type='circle'
                    width={30}
                    strokeWidth={9}
                    size='small'
                    format={percent => <span style={{
                      color: '#121212',
                      fontSize: '14px',
                    }}>{percent}</span>}
                  />
                ))}
              </div>
            </Row>
            <SectionHeader style={{marginBottom: 12, justifyContent: 'space-between'}}>
              <div>
                <span style={{marginRight: 39}}>FOCUS TERMS</span>
                <span>DISTRIBUTION</span>
                <span style={{marginLeft: 7}}><FontAwesomeIcon transform={{rotate: 140}} icon={faArrowUp} color='#A3A4A4' /></span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', fontWeight: 400}}>
                <Dot style={{backgroundColor: '#F44343'}}></Dot><span style={{marginRight: 14}}>0</span>
                <Dot style={{backgroundColor: '#FF8536'}}></Dot><span style={{marginRight: 14}}>1-3</span>
                <Dot style={{backgroundColor: '#BFE15E'}}></Dot><span style={{marginRight: 14}}>3-10</span>
                <Dot style={{backgroundColor: '#2AC155'}}></Dot><span style={{marginRight: 14}}>10+</span>
              </div>
            </SectionHeader>
            {analytics?.focusTermsGridData?.find(x => x?.keyword === selectedKeyword)?.focusTermUsageBySites?.map((item, i) => (
              <div style={{display: 'flex', gap: 2, marginTop: 2}} key={`square-${item.name}-${i}`}>
                <div style={{width: 241, marginLeft: 10}}>{item.name}</div>
                {item.values?.map((score, i) => (
                  <Tooltip overlayClassName={styles.tooltipOverlay} key={`${item.name}-score-${i}`} title={
                    focusTermsTooltip(analytics?.focusTermsGridData?.find(x => x?.keyword === selectedKeyword)?.sites?.[i]?.hostname, analytics?.focusTermsGridData?.find(x => x?.keyword === selectedKeyword)?.sites?.[i]?.url, item?.name, score)
                  }>
                    <Square isHovered={isHovered} onMouseEnter={() => {
                      setIsHovered(true);
                    }} onMouseLeave={() => {
                      setIsHovered(false);
                    }} style={{backgroundColor: getProgressColor(score ?? 0, 0, 3, 10)}}>{score}</Square>
                  </Tooltip>
                ))}
              </div>
            ))}
          </Wrapper>
        </TabPane>
        <TabPane tab={<div>Keyword suggestions <span style={{display: 'inline-block', marginLeft: 5}}>{currentPage?.isAnalyzing && <Spin indicator={antIcon} style={{marginRight: '20px'}}/>}</span></div>} key={'3'}>
          <Wrapper ref={keywordSuggestionsRef} style={{paddingTop: 0}}>
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
              <div>
                {/* <Title>Keywords Suggestions</Title> */}
                <Subtitle>Check out what keywords your competitors are using</Subtitle>
              </div>
              <Tooltip title='Export to PNG'>
                <Button
                  buttonType='transparent'
                  data-html2canvas-ignore={true}
                  onClick={() => exportSingleNode(keywordSuggestionsRef.current)}
                >
                  <FontAwesomeIcon icon={faFileDownload} style={{fontSize: '20px'}}/>
                </Button>
              </Tooltip>
            </div>
            <SectionHeader style={{marginTop: 30}}>
              <span style={{marginRight: 125}}>COMPETITOR RANK</span>
              <div style={{display: 'flex', gap: 2, alignItems: 'center'}}>
                {analytics?.keywordSuggestionsGridData?.find(x => x?.keyword === selectedKeyword)?.sites?.slice(0, 10).map((x, i) => (
                  <Rank key={`rank-${x?.url}-${i}`}>{i + 1}</Rank>
                ))}
              </div>
            </SectionHeader>
            <div style={{alignItems: 'start', textAlign: 'center', marginTop: 20, marginBottom: 20, marginLeft: 0, display: 'flex', gap: 2, flexDirection: 'column', transform: `rotate(-90deg) translate(0px, 243px)`, width: `${maxElemHeight}px`, maxHeight: `calc(${maxElemHeight}px - 19px)`}}>
              {analytics?.keywordSuggestionsGridData?.find(x => x?.keyword === selectedKeyword)?.sites?.slice(0, 10).map((x, i) => {
                return (
                  <a href={x?.url} target='_blank' rel='noreferrer' key={`site-${x?.url}-${i}`} style={{display: 'flex', marginBottom: '10px', marginTop: '11px'}} className='singleHostName'>
                    <Tooltip
                      overlayInnerStyle={{
                        backgroundColor: '#2d2f34',
                        boxShadow: 'rgb(0 0 0 / 20%) 1px 2px 10px',
                        borderRadius: '4px',
                      }}
                      title={
                        <div className={styles.tooltipContent}>
                          <a href={x.url} className={styles.title}>
                            {x.url}
                          </a>
                          <div className={styles.stat}>
                            <span className={styles.label}>Content Score: </span>
                            <span className={styles.value}>{x.contentScore}</span>
                          </div>
                          <div className={styles.stat}>
                            <span className={styles.label}>Word Count: </span>
                            <span className={styles.value}>{x.wordCount}</span>
                          </div>
                        </div>
                      }
                      key={x?.url}
                    >
                      <NextImg src={`https://www.google.com/s2/favicons?sz=16&domain_url=${x.hostname}`} style={{transform: 'rotate(90deg)', width: '16px', height: '16px', marginRight: '5px'}}/>
                    </Tooltip>
                    <Site>
                      {x?.hostname}
                    </Site>

                  </a>
                );
              })}
            </div>
            <SectionHeader>
              <span style={{marginRight: 45}}>KEYWORD</span>
            </SectionHeader>
            <Row wrap={false} style={{padding: '25px 10px'}}>
              {/* <div style={{width: 230, marginRight: 17}}>{selectedKeyword}</div> */}
              <div style={{width: 230, marginRight: 17}}>
                <SelectStyled suffixIcon={<CaretDownFilled />} value={selectedKeyword} onChange={value => setSelectedKeyword(value as string)}>
                  {analytics?.keywordSuggestionsGridData?.map((item, i) => (
                    <Select.Option key={`option-${item.keyword}-${i}`} value={item.keyword}>{item.keyword}</Select.Option>
                  ))}
                </SelectStyled>
              </div>
            </Row>
            <SectionHeader style={{marginBottom: 12, justifyContent: 'space-between'}}>
              <div>
                <span style={{marginRight: 39}}>TARGET KEYWORD</span>
                <span>USAGE</span>
                <span style={{marginLeft: 7}}><FontAwesomeIcon transform={{rotate: 140}} icon={faArrowUp} color='#A3A4A4' /></span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', fontWeight: 400}}>
                <Dot style={{backgroundColor: '#F44343'}}></Dot><span style={{marginRight: 14}}>Not Ranking</span>
                <Dot style={{backgroundColor: '#2AC155'}}></Dot><span style={{marginRight: 14}}>Ranking</span>
              </div>
            </SectionHeader>
            {analytics?.keywordSuggestionsGridData?.find(x => x?.keyword === selectedKeyword)?.keywordSuggestionsUsageBySites?.map((item, i) => (
              <div style={{display: 'flex', gap: 2, marginTop: 2}} key={`square-${item.name}-${i}`}>
                <div style={{width: 241, marginLeft: 10}}>{item.name}</div>
                {item.values?.map((score, i) => (
                  <Tooltip overlayClassName={styles.tooltipOverlay} key={`${item.name}-score-${i}`} title={
                    squareTooltip(analytics?.keywordSuggestionsGridData?.find(x => x?.keyword === selectedKeyword)?.sites?.[i]?.hostname, analytics?.keywordSuggestionsGridData?.find(x => x?.keyword === selectedKeyword)?.sites?.[i]?.url, item.name, score)
                  }>
                    <Square isHovered={isHovered} onMouseEnter={() => {
                      setIsHovered(true);
                    }} onMouseLeave={() => {
                      setIsHovered(false);
                    }} style={{backgroundColor: score ? '#2AC155' : '#F44343'}}></Square>
                  </Tooltip>
                ))}
              </div>
            ))}
          </Wrapper>
        </TabPane>
      </Tabs>
    </Drawer>
  );
});

const Title = styled.div`
  font-weight: 500;
  font-size: 20px;
  color: #121212;
  line-height: 28px;
`;

const TitleStyled = styled.div`
  font-weight: 600;
  font-size: 20px;
  color: #121212;
  line-height: normal;
  margin: 0 0 15px 30px;
`;

const Subtitle = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: #4E5156;
`;

const SectionHeader = styled.div`
  background: #e2e2e5;
  border-radius: 3px;
  display: flex;
  color: #121212;
  padding: 15px 10px;
  font-size: 12px;
  font-weight: 600;
`;

const Wrapper = styled.div`
  padding: 30px;
`;

const Square = styled.div<{isHovered: boolean}>`
  width: 40px;
  height: 25px;
  border-radius: 3px;
  background-color: red;
  cursor: pointer;
  color: transparent;
  
  opacity: ${p => p.isHovered && '0.5'};
  
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: white;
    opacity: 1;
  }
`;

const Dot = styled.div`
  border-radius: 50%;
  height: 5px;
  margin-right: 5px;
  width: 5px;
`;

const Rank = styled.div`
  background-color: #d3d3d7;
  width: 40px;
  height: 25px;
  font-size: 12px;
  color: #4E5156;
  justify-content: center;
  display: flex;
  align-items: center;
`;

const SelectStyled = styled(Select)`
  width: 100%;

  .ant-select-arrow {
    color: #121212;
  }
`;

const Site = styled.div`
  font-size: 12px;
  color: #2D6CCA;
  width: auto;
  display: block;
  align-items: left;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IsNotRanking= styled.span`
  padding: 2px 7px;
  background-color: #F44343;
  border-radius: 3px;
  color: #fff;
  margin-right: 5px;
`;

const IsRanking = styled.span`
  padding: 2px 7px;
  background-color: #1FAC47;
  border-radius: 3px;
  color: #fff;
  margin-right: 5px;
`;
